import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import swal from 'sweetalert'

import { CatchError } from 'Exceptions/exceptions'
import QrisConfigsService from 'Main/service/QrisConfigService'
import CommonListApiPaginated from 'views/components/Table/CommonListApiPaginated'
import { getRefreshToken } from 'variables/common'
import { QrisConfigModal } from './Modal/QrisConfigModal'

function QrisConfigsManager () {
  const navigate = useNavigate()
  const refreshToken = getRefreshToken()
  const qrisConfigsService = useMemo(() => new QrisConfigsService(), [])
  const [reloadKey, setReloadKey] = useState(0)
  const [paymentVendors, setPaymentVendors] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [modalIsAdd, setModalIsAdd] = useState(true)
  const [modalConfig, setModalConfig] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const getConfigs = useCallback(() => qrisConfigsService.getData(), [qrisConfigsService])

  const handleListError = useCallback((error) => {
    CatchError(error, navigate, refreshToken)
  }, [navigate, refreshToken])

  useEffect(() => {
    qrisConfigsService
      .getPaymentVendors()
      .then((response) => setPaymentVendors(Array.isArray(response.data) ? response.data : []))
      .catch(() => {
        /* dropdown just shows the placeholder option if this fails */
      })
  }, [qrisConfigsService])

  const handleAdd = () => {
    setModalIsAdd(true)
    setModalConfig(null)
    setModalOpen(true)
  }

  const handleEdit = useCallback(async (id) => {
    try {
      const response = await qrisConfigsService.getById(id)
      if (!response.data || !response.data.id) {
        swal('Error', 'Qris Config Not Found', 'error')
        return
      }
      setModalIsAdd(false)
      setModalConfig(response.data)
      setModalOpen(true)
    } catch (error) {
      const caught = CatchError(error, navigate, refreshToken)
      if (caught !== undefined) swal('Error', 'Failed to load QRIS config', 'error')
    }
  }, [navigate, qrisConfigsService, refreshToken])

  const handleModalSubmit = async (form) => {
    try {
      setSubmitting(true)
      if (modalIsAdd) await qrisConfigsService.create(form)
      else await qrisConfigsService.update(modalConfig.id, form)

      setModalOpen(false)
      await swal('Success!', modalIsAdd ? 'Qris Config Added' : 'Qris Config Updated', 'success')
      setReloadKey((current) => current + 1)
    } catch (error) {
      const caught = CatchError(error, navigate, refreshToken)
      if (caught !== undefined) swal('Error', 'Failed to save QRIS config', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = useCallback((id) => {
    swal('Delete Qris Config', 'Are You Sure Delete This Qris Config?', 'warning', {
      buttons: { cancel: 'Cancel', delete: { text: 'Delete', value: 'delete' } },
      dangerMode: true
    }).then(async (value) => {
      if (value !== 'delete') return

      try {
        await qrisConfigsService.deleteById(id)
        await swal('Success!', 'Qris Config Deleted', 'success')
        setReloadKey((current) => current + 1)
      } catch (error) {
        const caught = CatchError(error, navigate, refreshToken)
        if (caught !== undefined) swal('Error', 'Failed to delete QRIS config', 'error')
      }
    })
  }, [navigate, qrisConfigsService, refreshToken])

  const columns = useMemo(() => [
    { header: 'ID', accessorKey: 'id' },
    { header: 'Name', accessorKey: 'name' },
    { header: 'Client ID (API)', accessorKey: 'client_id' },
    { header: 'Nmid', accessorKey: 'nmid' },
    { header: 'Terminal Id', accessorKey: 'terminal_id' },
    {
      header: 'Enabled',
      accessorKey: 'enabled',
      cell: ({ getValue }) => getValue()
        ? <span className="status-success">Enabled</span>
        : <span className="status-danger">Disabled</span>
    },
    {
      header: 'Action',
      id: 'actions',
      enableSorting: false,
      filterable: false,
      exportable: false,
      cell: ({ row }) => (
        <div className="d-flex gap-1">
          <button type="button" onClick={() => handleEdit(row.original.id)} className="btn btn-primary" title="Edit Qris Config">
            <i className="fa-solid fa-pen-to-square" />
          </button>
          <button type="button" onClick={() => handleDelete(row.original.id)} className="btn btn-danger" title="Delete Qris Config">
            <i className="fa-solid fa-trash-can" />
          </button>
        </div>
      )
    }
  ], [handleDelete, handleEdit])

  const menu = (
    <div className="text-center">
      <button type="button" onClick={handleAdd} className="btn btn-success" title="Tambah Qris Config">
        <i className="fa-solid fa-pen-to-square" />&nbsp;&nbsp; Tambah Qris Config
      </button>
    </div>
  )

  return (
    <div className="page-content">
      <section>
        <CommonListApiPaginated
          header="List Qris Config"
          subtitle=""
          columns={columns}
          getData={getConfigs}
          menu={menu}
          reloadKey={reloadKey}
          loadingLabel="Loading QRIS configs..."
          emptyLabel="No QRIS configs found"
          onError={handleListError}
        />
      </section>

      <QrisConfigModal
        show={modalOpen}
        isAdd={modalIsAdd}
        config={modalConfig}
        vendors={paymentVendors}
        submitting={submitting}
        onClose={() => setModalOpen(false)}
        onSubmit={handleModalSubmit}
      />
    </div>
  )
}

export default QrisConfigsManager
